import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { fetchCollection, FALLBACK_NAVIGATION, fetchVirksomhedsoplysninger, FALLBACK_VIRKSOMHED } from '../lib/cms';
import { imageUrl } from '../lib/images';

export default function Header() {
  const [navItems, setNavItems] = useState(FALLBACK_NAVIGATION);
  const [virksomhed, setVirksomhed] = useState(FALLBACK_VIRKSOMHED);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isYdelserDropdownOpen, setIsYdelserDropdownOpen] = useState(false);
  const [isMobileYdelserOpen, setIsMobileYdelserOpen] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);
  const dropdownTimerRef = useRef(null);

  // Load navigation and company info from CMS with fallback
  useEffect(() => {
    async function loadData() {
      const [navData, virkData] = await Promise.all([
        fetchCollection('navigation', FALLBACK_NAVIGATION),
        fetchVirksomhedsoplysninger()
      ]);
      if (Array.isArray(navData) && navData.length > 0) {
        const sorted = [...navData].sort((a, b) => (Number(a.sortering) || 0) - (Number(b.sortering) || 0));
        setNavItems(sorted);
      }
      if (virkData) {
        setVirksomhed(virkData);
      }
    }
    loadData();
  }, []);

  // Stable scroll listener with hysteresis to completely eliminate any shaking/jumping
  // Triggers scrolled state only when scrollY > 60, resets only when scrollY < 20
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const y = window.scrollY;
          setIsScrolled((prev) => {
            if (!prev && y > 60) return true;
            if (prev && y < 20) return false;
            return prev;
          });
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock background scroll when mobile sidebar is open to avoid double scrollbars
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // Close desktop dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsYdelserDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsYdelserDropdownOpen(false);
  }, [location.pathname]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (dropdownTimerRef.current) clearTimeout(dropdownTimerRef.current);
    };
  }, []);

  const handleDropdownEnter = () => {
    if (dropdownTimerRef.current) clearTimeout(dropdownTimerRef.current);
    setIsYdelserDropdownOpen(true);
  };

  const handleDropdownLeave = () => {
    if (dropdownTimerRef.current) clearTimeout(dropdownTimerRef.current);
    dropdownTimerRef.current = setTimeout(() => {
      setIsYdelserDropdownOpen(false);
    }, 200);
  };

  // Main items without parent
  const mainItems = navItems.filter((item) => !item.parent && !item.fremhaevet_cta);
  // Sub-items for Ydelser
  const ydelserSubItems = navItems.filter(
    (item) => item.parent && (item.parent.toLowerCase() === 'ydelser' || item.parent === 'Ydelser')
  );

  const isLinkActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const isHome = location.pathname === '/';

  return (
    <header
      className={`sticky top-0 z-40 transition-colors duration-300 ${
        isHome && !isScrolled
          ? 'bg-white/95 backdrop-blur-md border-b border-slate-200/80 text-slate-800 shadow-sm'
          : 'bg-white/98 backdrop-blur-md border-b border-slate-200 text-slate-800 shadow-sm'
      }`}
    >
      {/* Top micro bar - smoothly collapses on scroll to save space without layout shift */}
      <div
        className={`hidden sm:block border-b px-4 text-xs font-medium overflow-hidden transition-all duration-300 ease-in-out ${
          isHome && !isScrolled
            ? 'bg-slate-50/90 border-slate-200/80 text-slate-600'
            : 'bg-slate-100 border-slate-200 text-slate-600'
        } ${
          isScrolled ? 'max-h-0 opacity-0 py-0 border-transparent' : 'max-h-12 opacity-100 py-1.5'
        }`}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4 sm:gap-6">
            <span className="flex items-center gap-1.5 text-slate-600">
              <i className="fa-solid fa-location-dot text-sky-600" aria-hidden="true" />
              <span className="hidden sm:inline">{virksomhed.adresse},</span> {virksomhed.postnr_by}
            </span>
            <span className="hidden md:inline-block text-slate-300">|</span>
            <span className="hidden md:flex items-center gap-1.5 text-slate-600">
              <i className="fa-solid fa-wrench text-sky-600" aria-hidden="true" />
              Specialløsninger i rustfri, sort & alu
            </span>
          </div>

          <div className="flex items-center gap-4">
            <a
              href={`tel:${virksomhed.telefon.replace(/\s+/g, '')}`}
              className="flex items-center gap-1.5 font-semibold text-slate-900 hover:text-sky-600 transition whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-sky-600" aria-hidden="true" />
              <span className="whitespace-nowrap">Tlf: {virksomhed.telefon}</span>
            </a>
            <span className="text-slate-300">|</span>
            <span className="text-slate-500 text-[11px] whitespace-nowrap font-mono">CVR: {virksomhed.cvr}</span>
          </div>
        </div>
      </div>

      {/* Main navigation container with smooth height transition */}
      <div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-colors duration-300 bg-transparent"
      >
        <div
          className={`flex items-center justify-between transition-all duration-300 ease-in-out ${
            isScrolled ? 'h-16' : 'h-20'
          }`}
        >
          {/* Logo with smooth size reduction when header is scrolled/sticky */}
          <Link
            to="/"
            className="flex items-center gap-3 group focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 rounded-lg p-1 transition-all"
          >
            <div className="relative flex items-center bg-[#050b07] px-3.5 py-1.5 sm:px-4 sm:py-2 rounded-xl border border-slate-900 shadow-md group-hover:bg-black transition-all">
              <img
                src={imageUrl(virksomhed.logo?.[0] || '/_assets/duus-logo-transparent.png', 'width=500,quality=100,format=auto')}
                alt={`${virksomhed.firmanavn} Logo - Rustfri, Sort & Alu`}
                className={`w-auto object-contain transition-all duration-300 ease-in-out ${
                  isScrolled ? 'h-9 md:h-10' : 'h-11 sm:h-12 md:h-13'
                }`}
              />
            </div>
          </Link>

          {/* Desktop Navigation: VISIBLE ONLY ON >= 1200px */}
          <nav className="hidden min-[1200px]:flex items-center gap-1" aria-label="Hovedmenu">
            {mainItems.map((item) => {
              const isYdelser = item.sti === '/ydelser';
              const active = isLinkActive(item.sti);

              if (isYdelser && ydelserSubItems.length > 0) {
                return (
                  <div
                    key={item.id || item.sti}
                    className="relative"
                    ref={dropdownRef}
                    onMouseEnter={handleDropdownEnter}
                    onMouseLeave={handleDropdownLeave}
                  >
                    <div className="flex items-center">
                      <Link
                        to={item.sti}
                        className={`px-3.5 py-2 text-sm font-medium rounded-l-md transition-colors ${
                          active
                            ? 'text-slate-950 bg-slate-100 font-semibold'
                            : 'text-slate-700 hover:text-slate-950 hover:bg-slate-100/70'
                        }`}
                      >
                        {item.titel}
                      </Link>
                      <button
                        type="button"
                        onClick={() => setIsYdelserDropdownOpen((prev) => !prev)}
                        className={`px-2 py-2 text-sm rounded-r-md transition-colors focus:outline-none cursor-pointer ${
                          active
                            ? 'text-slate-950 bg-slate-100'
                            : 'text-slate-500 hover:text-slate-950 hover:bg-slate-100/70'
                        }`}
                        aria-expanded={isYdelserDropdownOpen}
                        aria-label="Åbn ydelser undermenu"
                      >
                        <i
                          className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                            isYdelserDropdownOpen ? 'rotate-180 text-sky-600' : ''
                          }`}
                          aria-hidden="true"
                        />
                      </button>
                    </div>

                    {/* Dropdown Menu with hit-bridge to prevent closing when cursor moves into it */}
                    {isYdelserDropdownOpen && (
                      <div
                        className="absolute top-full left-0 w-76 pt-2 z-50 animate-in fade-in slide-in-from-top-1 duration-150 before:absolute before:-top-3 before:left-0 before:w-full before:h-4 before:content-['']"
                        onMouseEnter={handleDropdownEnter}
                        onMouseLeave={handleDropdownLeave}
                      >
                        <div className="bg-white border border-slate-200 rounded-xl shadow-2xl p-2.5 space-y-1">
                          <Link
                            to="/ydelser"
                            onClick={() => setIsYdelserDropdownOpen(false)}
                            className={`block px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-md transition-colors border-b border-slate-100 mb-1.5 ${
                              location.pathname === '/ydelser'
                                ? 'text-sky-700 bg-sky-50 border-sky-200'
                                : 'text-sky-600 hover:text-sky-700 hover:bg-slate-50'
                            }`}
                          >
                            Se alle ydelser →
                          </Link>
                          {ydelserSubItems.map((sub) => {
                            const subActive = location.pathname === sub.sti;
                            return (
                              <Link
                                key={sub.id || sub.sti}
                                to={sub.sti}
                                onClick={() => setIsYdelserDropdownOpen(false)}
                                className={`flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-all duration-150 ${
                                  subActive
                                    ? 'bg-sky-50 text-sky-800 font-semibold border-l-2 border-sky-600 pl-3.5 ring-1 ring-sky-200'
                                    : 'text-slate-700 hover:text-slate-950 hover:bg-slate-50 border-l-2 border-transparent pl-3.5'
                                }`}
                              >
                                <i
                                  className={`fa-solid fa-angles-right text-xs transition-colors ${
                                    subActive ? 'text-sky-600' : 'text-slate-400'
                                  }`}
                                  aria-hidden="true"
                                />
                                <span>{sub.titel}</span>
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.id || item.sti}
                  to={item.sti}
                  className={`px-3.5 py-2 text-sm font-medium rounded-md transition-colors ${
                    active
                      ? 'text-slate-950 bg-slate-100 font-semibold'
                      : 'text-slate-700 hover:text-slate-950 hover:bg-slate-100/70'
                  }`}
                >
                  {item.titel}
                </Link>
              );
            })}
          </nav>

          {/* Desktop Right Side CTA & Phone */}
          <div className="hidden min-[1200px]:flex items-center gap-4">
            <a
              href="tel:30271671"
              className="flex items-center gap-2 text-slate-700 hover:text-slate-950 text-sm font-medium px-3 py-2 rounded-md hover:bg-slate-100/80 transition whitespace-nowrap"
            >
              <span className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-sky-600">
                <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
              </span>
              <span className="whitespace-nowrap font-semibold">30 27 16 71</span>
            </a>

            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-slate-900 hover:bg-black text-white text-sm font-semibold shadow-md transition transform hover:-translate-y-0.5 active:translate-y-0 cursor-pointer"
            >
              <span>Få et tilbud</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

          {/* Mobile Burger Menu Button: VISIBLE ONLY UNDER 1200px */}
          <div className="flex min-[1200px]:hidden items-center gap-2.5">
            <a
              href="tel:30271671"
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-slate-100 text-sky-700 border border-slate-200 whitespace-nowrap"
              aria-label="Ring til 30 27 16 71"
            >
              <i className="fa-solid fa-phone" aria-hidden="true" />
              <span className="whitespace-nowrap">30 27 16 71</span>
            </a>

            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2.5 rounded-lg bg-slate-100 text-slate-700 hover:text-slate-950 hover:bg-slate-200 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500 cursor-pointer"
              aria-expanded={isMobileMenuOpen}
              aria-label={isMobileMenuOpen ? 'Luk navigationsmenu' : 'Åbn navigationsmenu'}
            >
              <i
                className={`fa-solid ${isMobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-xl`}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer / Sidebar Menu (< 1200px) */}
      {/* Independently scrollable so all items are reachable on any screen height */}
      {isMobileMenuOpen && (
        <div
          className="min-[1200px]:hidden max-h-[calc(100dvh-4.5rem)] overflow-y-auto overflow-x-hidden overscroll-contain bg-white/98 backdrop-blur-xl border-b border-slate-200 px-4 pt-3 pb-8 space-y-4 animate-in slide-in-from-top-2 duration-200 shadow-2xl"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <nav className="flex flex-col space-y-1.5" aria-label="Mobil menu">
            {mainItems.map((item) => {
              const isYdelser = item.sti === '/ydelser';
              const active = isLinkActive(item.sti);

              if (isYdelser && ydelserSubItems.length > 0) {
                return (
                  <div key={item.id || item.sti} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <Link
                        to={item.sti}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`flex-1 px-3.5 py-2.5 text-base font-medium rounded-lg transition-colors ${
                          active
                            ? 'bg-slate-100 text-slate-950 font-semibold'
                            : 'text-slate-700 hover:bg-slate-50 hover:text-slate-950'
                        }`}
                      >
                        {item.titel}
                      </Link>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsMobileYdelserOpen((prev) => !prev);
                        }}
                        className="p-2.5 text-slate-500 hover:text-slate-950 rounded-md hover:bg-slate-100 transition-colors cursor-pointer"
                        aria-label={isMobileYdelserOpen ? 'Skjul ydelser undermenu' : 'Vis ydelser undermenu'}
                      >
                        <i
                          className={`fa-solid fa-chevron-down text-sm transition-transform duration-200 ${
                            isMobileYdelserOpen ? 'rotate-180 text-sky-600' : ''
                          }`}
                          aria-hidden="true"
                        />
                      </button>
                    </div>

                    {/* Submenu links with clear active color highlight & reliable navigation */}
                    {isMobileYdelserOpen && (
                      <div className="pl-3 pr-1 py-1 space-y-1 border-l-2 border-slate-200 ml-3.5 mt-1">
                        <Link
                          to="/ydelser"
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={`block px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-md transition-colors ${
                            location.pathname === '/ydelser'
                              ? 'bg-sky-50 text-sky-700 font-bold border-l-2 border-sky-600 pl-2.5'
                              : 'text-sky-600 hover:text-sky-700 hover:bg-slate-50'
                          }`}
                        >
                          Se alle ydelser →
                        </Link>

                        {ydelserSubItems.map((sub) => {
                          const subActive = location.pathname === sub.sti;
                          return (
                            <Link
                              key={sub.id || sub.sti}
                              to={sub.sti}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className={`flex items-center gap-2.5 px-3 py-2.5 text-sm rounded-lg transition-colors ${
                                subActive
                                  ? 'bg-sky-50 text-sky-800 font-semibold border-l-2 border-sky-600 pl-2.5 ring-1 ring-sky-200'
                                  : 'text-slate-700 hover:text-slate-950 hover:bg-slate-50 border-l-2 border-transparent pl-2.5'
                              }`}
                            >
                              <i
                                className={`fa-solid fa-angles-right text-xs transition-colors ${
                                  subActive ? 'text-sky-600' : 'text-slate-400'
                                }`}
                                aria-hidden="true"
                              />
                              <span>{sub.titel}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.id || item.sti}
                  to={item.sti}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`px-3.5 py-2.5 text-base font-medium rounded-lg transition-colors ${
                    active
                      ? 'bg-slate-100 text-slate-950 font-semibold'
                      : 'text-slate-700 hover:bg-slate-50 hover:text-slate-950'
                  }`}
                >
                  {item.titel}
                </Link>
              );
            })}
          </nav>

          {/* Action CTAs inside mobile drawer */}
          <div className="pt-4 border-t border-slate-200 flex flex-col gap-3">
            <a
              href={`tel:${virksomhed.telefon.replace(/\s+/g, '')}`}
              className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-slate-100 border border-slate-200 text-slate-800 font-semibold hover:bg-slate-200 transition whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-sky-600" aria-hidden="true" />
              <span className="whitespace-nowrap">Ring {virksomhed.telefon}</span>
            </a>

            <Link
              to="/kontakt"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-slate-900 text-white font-semibold shadow-md hover:bg-black transition cursor-pointer"
            >
              <span>Få et uforpligtende tilbud</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
